import CasesApi from "@/api/cases";
import UserApi from "@/api/users/users";
import {
  Option,
  SelectBox,
  SelectBoxProps,
  styled,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";

type UserRecord = {
  user_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  title: string;
};

export type UserSelectorProps = Omit<
  SelectBoxProps,
  | "data"
  | "renderOption"
  | "OptionTooltip"
  | "searchFn"
  | "onScroll"
  | "searchable"
  | "loading"
  | "defaultValue"
  | "value"
> & {
  defaultValue?: Option;
  value?: Option;
  query?: { includeObservers?: boolean; includeInactive?: boolean };
};

const TooltipContent = styled(
  ({ className, data }: { className?: string; data: UserRecord }) => {
    return (
      <div className={className}>
        <div className="detail-title">User Details</div>
        <div className="detail-item">
          <div className="detail-label">Name</div>
          <div className="detail-value">{data.full_name || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Email</div>
          <div className="detail-value">{data.email || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Title</div>
          <div className="detail-value">{data.title || "None"}</div>
        </div>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: fit-content;
  max-width: 300px;

  .detail-title {
    font-weight: bold;
    font-size: 15px;
  }
  .detail-item {
    display: flex;
    flex-direction: column;
  }

  .detail-label {
    font-weight: bold;
    min-width: 150px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

// Render like RoleSelector via renderOption prop
const UserOptionComponent = ({ option }: { option: Partial<Option> }) => {
  const theme = useTheme();
  if (typeof option === "string") return <div>{option}</div>;
  const record = (option.data || {}) as UserRecord;
  return (
    <div style={{ padding: "5px 0" }}>
      <div style={{ fontWeight: 500 }}>{record.full_name || option.label}</div>
      <div style={{ fontSize: "12px", color: theme.palette.text.secondary }}>
        {record.email || ""}
      </div>
    </div>
  );
};

export const UserSelector: React.FC<UserSelectorProps> = (props) => {
  const [search, setSearch] = useState("");

  const query = {
    ...props.query,
    search,
    data_only: false,
    pageSize: 1000,
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ["users", "list", query],
      queryFn: ({ pageParam = 1 }) => {
        return UserApi.getUsers({
          ...query,
          page: pageParam,
        });
      },
      getNextPageParam: (lastPage) => lastPage.nextPage,
      getPreviousPageParam: (firstPage: any) => {
        if (firstPage.page - 1 === 0) return null;
        return firstPage.page - 1;
      },
      initialPageParam: 1,
    });

  const records: UserRecord[] =
    data?.pages?.reduce((acc, page) => {
      return [...acc, ...page.data];
    }, []) || [];

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (isFetchingNextPage) return;
    if (!hasNextPage) return;
    fetchNextPage();
  }, 50);

  const debouncedSearch = useDebouncedCallback(
    (val: string) => setSearch(val || ""),
    300
  );

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <=
      clientHeight + 100 * ((data?.pages?.length as number) || 0)
    ) {
      debouncedFetchNextPage();
    }
  };

  // Combine number and name in label for client-side search by either
  const options: Option[] = records.map((item) => ({
    label: `${item.full_name}`,
    value: item.user_id,
    data: item,
  }));

  return (
    <SelectBox
      placeholder="Select or search for a user..."
      {...props}
      data={options}
      onScroll={handleScroll}
      searchable={true}
      searchFn={debouncedSearch}
      loading={isLoading}
      renderOption={(item) => {
        if (typeof item === "string") {
          return <div>{item}</div>;
        }
        return <UserOptionComponent option={item} />;
      }}
      OptionTooltip={TooltipContent}
    />
  );
};
